import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import {
  getDistanceUsingHaversine,
  getUserReadableDateTimeFormat,
} from '../../../../global.variable';
import { UsersService } from '../../../pages/pages/users/users.service';
import { ToasterService } from '../../services/toaster.service';
import { ViewUserDetailComponent } from '../view-user-detail/view-user-detail.component';

@Component({
  selector: 'gtapp-user-trail',
  standalone: false,
  templateUrl: './user-trail.component.html',
  styleUrl: './user-trail.component.scss',
})
export class UserTrailComponent implements OnDestroy, OnChanges, AfterViewInit {
  @Input() selectedUser: any = {};
  @Input() dateRange: any = {};

  selectedGuardUpdateHistoryList: any = [];

  guardStats: any = {};

  userDetailPopoverTemplate = ViewUserDetailComponent;

  moreEventsAvailable: boolean = false;
  previousRowsFetched: number = 0;

  selectedUpdate: any = {};

  @ViewChildren('eventsList') itemRefs!: QueryList<ElementRef>;
  @ViewChild('eventsContainer') containerRef!: ElementRef;

  observer!: IntersectionObserver;

  visibleEventsMappedList: any = [];
  visibleItemTimeout: any;

  selectedEvent: any;

  constructor(
    private userService: UsersService,
    private toasterService: ToasterService,
  ) {}
  ngOnChanges(changes: SimpleChanges): void {
    this.getUserUpdateLog();
  }
  ngOnDestroy(): void {}

  ngAfterViewInit(): void {
    this.itemRefs?.changes?.subscribe(() => {
      this.initObserver();
    });
  }
  initObserver() {
    if (!this.containerRef) return;

    const container = this.containerRef.nativeElement;

    // disconnect old observer
    if (this.observer) {
      this.observer.disconnect();
    }

    const visibleItems = new Set<Number>();

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const el = entry.target as HTMLElement;

          const index = Number(el?.dataset['index']);

          if (entry.isIntersecting) {
            visibleItems.add(index);
          } else {
            visibleItems.delete(index);
          }
        });
        this.handleScrollStop(visibleItems);
      },
      {
        root: container,
        threshold: 0,
      },
    );

    this.itemRefs.forEach((item) => {
      this.observer!.observe(item.nativeElement);
    });
  }
  handleScrollStop(visibleItems: any) {
    clearTimeout(this.visibleItemTimeout);

    this.visibleItemTimeout = setTimeout(() => {
      this.visibleEventsMappedList = [...visibleItems]
        ?.sort((a: any, b: any) => a - b)
        ?.map((index: any) => ({
          order: index + 1,
          ...this.selectedGuardUpdateHistoryList[index],
        }));
    }, 300);
  }
  formatUserUpdates() {
    if (this.selectedGuardUpdateHistoryList?.length) {
      let totalDistance: any = 0;
      let distance: any = 0;
      let updatesByDate: any = {};
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      let lastGps: any;

      for (let i = 0; i < this.selectedGuardUpdateHistoryList.length; i++) {
        const curr = this.selectedGuardUpdateHistoryList[i];
        if (i > 0) {
          if (curr?.gps?.lat && curr?.gps?.lon) {
            if (lastGps) {
              distance = Number(
                getDistanceUsingHaversine(
                  lastGps.lat,
                  curr?.gps.lat,
                  lastGps.lon,
                  curr?.gps.lon,
                ),
              );
              totalDistance += distance;
            }

            lastGps = curr?.gps;
          }
        }

        const updateDate = new Date(curr?.updated_at);
        updateDate.setHours(0, 0, 0, 0);

        const dictDateKey = getUserReadableDateTimeFormat(updateDate);

        // Initialize details object for the date if not present
        if (!updatesByDate[dictDateKey]) {
          updatesByDate[dictDateKey] = {
            distance: 0,
            updateCount: 0,
            fileUploaded: 0,
            commentAdded: 0,
            checkpointScanned: 0,
            patrolUpdateCount: 0,
            jobUpdateCount: 0,
            incidentUpdateCount: 0,
          };
        }
        if (curr?.event_type?.toLowerCase() === 'patrol update') {
          updatesByDate[dictDateKey]['patrolUpdateCount'] += 1;
          switch (curr?.event_action) {
            case 0:
              updatesByDate[dictDateKey]['checkpointScanned'] += 1;
              break;
            case 1:
              updatesByDate[dictDateKey]['commentAdded'] += 1;
              break;
            case 2:
              updatesByDate[dictDateKey]['fileUploaded'] += 1;
              break;
            case 6:
              updatesByDate[dictDateKey]['checkpointScanned'] += 1;
              break;
            case 7:
              updatesByDate[dictDateKey]['fileUploaded'] += 1;
              break;
            case 12:
              updatesByDate[dictDateKey]['checkpointScanned'] += 1;
              break;
          }
        }
        if (curr?.event_type?.toLowerCase() === 'job update') {
          updatesByDate[dictDateKey]['jobUpdateCount'] += 1;
          switch (curr?.event_action) {
            case 7:
              updatesByDate[dictDateKey]['commentAdded'] += 1;
              break;
            case 8:
              updatesByDate[dictDateKey]['fileUploaded'] += 1;
              break;

            case 19:
              updatesByDate[dictDateKey]['fileUploaded'] += 1;
              break;
          }
        }
        if (curr?.event_type?.toLowerCase() === 'incident update') {
          updatesByDate[dictDateKey]['incidentUpdateCount'] += 1;
        }

        updatesByDate[dictDateKey]['distance'] += distance;
        updatesByDate[dictDateKey]['updateCount'] += 1;
      }

      const weekDays = Object.keys(updatesByDate);
      this.guardStats = {
        updatesByDate: updatesByDate,
        totalDistance: totalDistance,
      };
      const weekDaysLength = weekDays?.length;
      if (weekDaysLength > 1) {
        this.guardStats['avgDistancePerDay'] = totalDistance / weekDaysLength;
        this.guardStats['avgUpdatePerDay'] =
          this.selectedGuardUpdateHistoryList?.length / weekDaysLength;
      }
    }
  }
  getUserUpdateLog(loadMore = false) {
    const fetchRowCount: number = 10;
    if (loadMore) {
      this.previousRowsFetched = this.previousRowsFetched + fetchRowCount;
    }

    let body: any = {
      event_type: 'user_update_history',
      search_filters: {},
      sub_user_id: this.selectedUser?.id,
      date_range: this.dateRange,
      rows: fetchRowCount + 1,
      previous: this.previousRowsFetched,
    };

    this.userService.getUserUpdateLog(body).subscribe((response: any) => {
      if (response['status'] == 'success') {
        const responseData: any = response?.data || [];
        this.moreEventsAvailable = responseData?.length > fetchRowCount;
        this.selectedGuardUpdateHistoryList = [
          ...this.selectedGuardUpdateHistoryList,
          ...responseData?.slice(0, 10),
        ];

        this.formatUserUpdates();
      } else {
        this.toasterService.setMessage({
          successMessage: '',
          errorMessage: response['message'],
        });
      }
    });
  }
  zoomIntoMap(updateData: any) {
    this.selectedUpdate = { ...updateData };
  }
  showUpdateDetails(event: any) {
    if (event?.id == this.selectedEvent?.id) return;

    this.selectedEvent = { ...event };

    if (event?.id && event?.event_type) {
      // call backend to get detailed info if its event log
      let body: any = {
        event_type: 'user_update_history_detail',
        update_type: event?.event_type,
        event_id: btoa(event?.id),
      };
      this.userService.getUserUpdateLog(body).subscribe((response: any) => {
        if (response['status'] == 'success') {
          this.selectedEvent = { ...event, ...response?.data };
        } else {
          this.toasterService.setMessage({
            successMessage: '',
            errorMessage: response['message'],
          });
        }
      });
    }
  }
}
